import { useState, useEffect } from "react";
import { getRDOs } from "@/services/rdoStorageSupabase";
import { getObraById } from "@/lib/obraCache";
import { formatYmd } from "@/utils/dateUtils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { LayoutGrid, List, Search, Bell, Clock, MapPin, X } from "lucide-react";
import type { WorkStatus, Work } from "@/pages/Works";

interface WorksFiltersProps {
  selectedStatus: WorkStatus | "todas";
  onStatusChange: (status: WorkStatus | "todas") => void;
  works: Work[];
  viewMode: "grid" | "list";
  onViewModeChange: (mode: "grid" | "list") => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
}

interface UpdateItem {
  id: string;
  workName: string;
  type: string;
  description: string;
  time: string;
  location: string;
  date: string;
}

export const WorksFilters = ({ selectedStatus, onStatusChange, works, viewMode, onViewModeChange, searchTerm, onSearchChange }: WorksFiltersProps) => {
  const [isUpdatesOpen, setIsUpdatesOpen] = useState(false);
  const [isAllAlertsOpen, setIsAllAlertsOpen] = useState(false);

  // Atualizações reais: RDOs mais recentes das obras do usuário
  const [recentUpdates, setRecentUpdates] = useState<UpdateItem[]>([]);
  useEffect(() => {
    let active = true;
    getRDOs()
      .then((rdos) => {
        if (!active) return;
        setRecentUpdates(
          rdos
            .slice()
            .sort((a, b) => (b.updatedAt ?? "").localeCompare(a.updatedAt ?? ""))
            .slice(0, 30)
            .map((rdo) => {
              const obra = getObraById(rdo.obraId);
              const description = rdo.approved
                ? "Relatório aprovado"
                : rdo.status === "in_review"
                ? "Relatório em revisão"
                : "Relatório preenchido";
              return {
                id: rdo.id,
                workName: obra?.nomeObra ?? "Obra",
                type: "Relatório",
                description,
                time: rdo.updatedAt ? new Date(rdo.updatedAt).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "",
                location: obra?.endereco?.cidade ?? "",
                date: formatYmd(rdo.date),
              };
            })
        );
      })
      .catch((e) => console.error("WorksFilters updates:", e));
    return () => { active = false; };
  }, []);
  const allAlerts = recentUpdates;

  const getUpdateTypeColor = (type: string) => {
    switch (type) {
      case "Progresso": return "bg-blue-100 text-blue-800";
      case "Status": return "bg-yellow-100 text-yellow-800";
      case "Conclusão": return "bg-green-100 text-green-800";
      case "Relatório": return "bg-purple-100 text-purple-800";
      case "Ocorrência": return "bg-red-100 text-red-800";
      case "Tarefa": return "bg-indigo-100 text-indigo-800";
      case "Material": return "bg-orange-100 text-orange-800";
      case "Equipe": return "bg-cyan-100 text-cyan-800";
      case "Inspeção": return "bg-emerald-100 text-emerald-800";
      case "Aprovação": return "bg-green-200 text-green-900";
      case "Atraso": return "bg-red-200 text-red-900";
      case "Revisão": return "bg-amber-100 text-amber-800";
      case "Reunião": return "bg-violet-100 text-violet-800";
      case "Documentação": return "bg-slate-100 text-slate-800";
      case "Qualidade": return "bg-teal-100 text-teal-800";
      case "Cronograma": return "bg-pink-100 text-pink-800";
      case "Segurança": return "bg-rose-100 text-rose-800";
      case "Licença": return "bg-lime-100 text-lime-800";
      case "Orçamento": return "bg-sky-100 text-sky-800";
      case "Fornecedor": return "bg-fuchsia-100 text-fuchsia-800";
      case "Alteração": return "bg-indigo-200 text-indigo-900";
      case "Entrega": return "bg-emerald-200 text-emerald-900";
      case "Manutenção": return "bg-gray-200 text-gray-900";
      case "Comunicado": return "bg-blue-200 text-blue-900";
      case "Marcos": return "bg-purple-200 text-purple-900";
      case "Treinamento": return "bg-orange-200 text-orange-900";
      case "Finalização": return "bg-green-300 text-green-900";
      case "Início": return "bg-blue-300 text-blue-900";
      case "Planejamento": return "bg-cyan-200 text-cyan-900";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  // Contar obras por status
  const statusCounts = {
    "em-andamento": works.filter(w => w.status === "em-andamento").length,
    "paralisada": works.filter(w => w.status === "paralisada").length,
    "concluida": works.filter(w => w.status === "concluida").length,
    "todas": works.length
  };

  const filters = [
    {
      key: "todas" as const,
      label: "Todas",
      count: statusCounts.todas,
      bgColor: "bg-primary",
      textColor: "text-white"
    },
    {
      key: "em-andamento" as const,
      label: "Em Andamento",
      count: statusCounts["em-andamento"],
      bgColor: "bg-success",
      textColor: "text-white"
    },
    {
      key: "paralisada" as const,
      label: "Paralisada",
      count: statusCounts.paralisada,
      bgColor: "bg-warning",
      textColor: "text-white"
    },
    {
      key: "concluida" as const,
      label: "Concluída",
      count: statusCounts.concluida,
      bgColor: "bg-muted",
      textColor: "text-foreground"
    }
  ];

  return (
    <div className="space-y-4">
      {/* Filtros de Status */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        {filters.map((filter) => {
          const isActive = selectedStatus === filter.key;
          
          return (
            <Button
              key={filter.key}
              onClick={() => onStatusChange(filter.key)}
              className={`px-4 py-2 rounded-full font-medium text-sm ${
                isActive 
                  ? `${filter.bgColor} ${filter.textColor} hover:opacity-90` 
                  : "bg-background border border-border text-muted-foreground hover:bg-accent"
              }`}
              variant="ghost"
            >
              {filter.label}
            </Button>
          );
        })}
        
        {/* Busca e Sino - posicionados entre os filtros e botões de visualização */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto sm:ml-auto sm:mr-4">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder="Buscar obra por nome, cliente, cidade..." 
              value={searchTerm} 
              onChange={e => onSearchChange(e.target.value)} 
              className="pl-10 w-full sm:w-80"
            />
          </div>
          
          <Popover open={isUpdatesOpen} onOpenChange={setIsUpdatesOpen}>
            <PopoverTrigger asChild>
              <div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="relative">
                        <Bell className="h-5 w-5" />
                        {recentUpdates.length > 0 && (
                          <Badge 
                            variant="destructive" 
                            className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
                          >
                            {recentUpdates.length}
                          </Badge>
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Últimas atualizações das obras</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </PopoverTrigger>
            <PopoverContent className="w-96 p-0 bg-background border" align="end">
              <div className="p-4 border-b">
                <h3 className="font-semibold text-sm">Últimas Atualizações</h3>
                <p className="text-xs text-muted-foreground">10 atualizações mais recentes das suas obras</p>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {recentUpdates.length === 0 && (
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    Nenhuma atualização ainda.
                  </div>
                )}
                {recentUpdates.map((update) => (
                  <div key={update.id} className="p-3 border-b last:border-b-0 hover:bg-accent/50 transition-colors">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={`text-xs px-2 py-0.5 ${getUpdateTypeColor(update.type)}`}>
                            {update.type}
                          </Badge>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {update.time}
                          </div>
                        </div>
                        <p className="text-sm font-medium text-foreground truncate">{update.workName}</p>
                        <p className="text-xs text-muted-foreground mb-1">{update.description}</p>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3" />
                          {update.location}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-3 border-t bg-muted/30">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full text-xs"
                  onClick={() => setIsAllAlertsOpen(true)}
                >
                  Ver todos os alertas
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
        
        {/* Botões de visualização */}
        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className={viewMode === "grid" ? "text-primary bg-primary/10" : "text-muted-foreground"}
                  onClick={() => onViewModeChange("grid")}
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Quadro</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className={viewMode === "list" ? "text-primary bg-primary/10" : "text-muted-foreground"}
                  onClick={() => onViewModeChange("list")}
                >
                  <List className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Lista</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Contador de obras */}
      <p className="text-sm text-muted-foreground">
        {statusCounts.todas} obras encontradas
      </p>

      {/* All Alerts Dialog */}
      <Dialog open={isAllAlertsOpen} onOpenChange={setIsAllAlertsOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-hidden">
          <DialogHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <div>
              <DialogTitle className="text-lg">Todos os Alertas</DialogTitle>
              <p className="text-sm text-muted-foreground">
                Últimas 30 atualizações das obras que você participa
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsAllAlertsOpen(false)}
              className="h-6 w-6"
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogHeader>
          
          <ScrollArea className="h-[60vh] w-full pr-4">
            <div className="space-y-1">
              {allAlerts.map((alert, index) => (
                <div
                  key={alert.id}
                  className={`p-3 rounded-lg hover:bg-accent/50 transition-colors border-l-4 ${
                    index < 5 ? 'border-l-primary' : 'border-l-muted'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={`text-xs px-2 py-0.5 ${getUpdateTypeColor(alert.type)}`}>
                          {alert.type}
                        </Badge>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {alert.time}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {alert.date}
                        </div>
                      </div>
                      
                      <div className="space-y-1">
                        <p className="font-medium text-sm text-foreground line-clamp-1">
                          {alert.workName}
                        </p>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {alert.description}
                        </p>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3" />
                          {alert.location}
                        </div>
                      </div>
                    </div>
                    
                    {index < 5 && (
                      <div className="flex-shrink-0">
                        <Badge variant="secondary" className="text-xs">
                          Recente
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
          
          <div className="flex justify-between items-center pt-4 border-t">
            <p className="text-xs text-muted-foreground">
              Mostrando {allAlerts.length} de {allAlerts.length} alertas
            </p>
            <Button variant="outline" size="sm" onClick={() => setIsAllAlertsOpen(false)}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};